import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCamera, FiAlertCircle, FiUsers, FiX, FiShield, FiEyeOff } = FiIcons;

function ScreenshotProtection() {
  const [showWarning, setShowWarning] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [screenshotBlocked, setScreenshotBlocked] = useState(false);

  useEffect(() => {
    // Block all screenshot attempts
    const blockScreenshot = () => {
      setScreenshotBlocked(true);
      setShowWarning(true);
      
      const x = window.innerWidth / 2 - 250;
      const y = window.innerHeight / 2 - 150;
      setPosition({ x, y });

      // Auto-hide after 5 seconds
      const timer = setTimeout(() => {
        setShowWarning(false);
        setScreenshotBlocked(false);
      }, 5000);
      return () => clearTimeout(timer);
    };

    // Handle visibility change (when user tries to screenshot)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && !screenshotBlocked) {
        blockScreenshot();
      }
    };

    // Handle PrintScreen key and all screenshot shortcuts
    const handleKeyDown = (e) => {
      // Block PrintScreen
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        e.stopImmediatePropagation();
        blockScreenshot();
        return false;
      }

      // Block Windows screenshot shortcuts
      if (e.ctrlKey && e.key === 'PrintScreen') {
        e.preventDefault();
        e.stopImmediatePropagation();
        blockScreenshot();
        return false;
      }

      if (e.altKey && e.key === 'PrintScreen') {
        e.preventDefault();
        e.stopImmediatePropagation();
        blockScreenshot();
        return false;
      }

      if (e.shiftKey && e.key === 'PrintScreen') {
        e.preventDefault();
        e.stopImmediatePropagation();
        blockScreenshot();
        return false;
      }

      // Block Mac screenshot shortcuts
      if (e.metaKey && e.shiftKey && e.key === '3') {
        e.preventDefault();
        e.stopImmediatePropagation();
        blockScreenshot();
        return false;
      }

      if (e.metaKey && e.shiftKey && e.key === '4') {
        e.preventDefault();
        e.stopImmediatePropagation();
        blockScreenshot();
        return false;
      }

      if (e.metaKey && e.shiftKey && e.key === '5') {
        e.preventDefault();
        e.stopImmediatePropagation();
        blockScreenshot();
        return false;
      }

      // Block Snipping Tool shortcuts
      if (e.metaKey && e.shiftKey && e.key === 's') {
        e.preventDefault();
        e.stopImmediatePropagation();
        blockScreenshot();
        return false;
      }

      // Block Win + Shift + S (Windows Snipping Tool)
      if (e.metaKey && e.shiftKey && e.key === 'S') {
        e.preventDefault();
        e.stopImmediatePropagation();
        blockScreenshot();
        return false;
      }
    };

    // Handle key up to clear clipboard after screenshot attempt
    const handleKeyUp = (e) => {
      if (e.key === 'PrintScreen') {
        // Clear clipboard
        navigator.clipboard.writeText('').catch(() => {});
      }
    };

    // Prevent right-click context menu
    const handleContextMenu = (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      return false;
    };

    // Prevent drag and drop
    const handleDragStart = (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      return false;
    };

    // Add CSS protection styles
    const addProtectionStyles = () => {
      const style = document.createElement('style');
      style.innerHTML = `
        * {
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          user-select: none !important;
          -webkit-touch-callout: none !important;
          -webkit-tap-highlight-color: transparent !important;
        }
        
        body * {
          pointer-events: auto !important;
        }
        
        img, svg, video, canvas {
          -webkit-user-drag: none !important;
          -khtml-user-drag: none !important;
          -moz-user-drag: none !important;
          -o-user-drag: none !important;
          user-drag: none !important;
          pointer-events: none !important;
        }
        
        @media print {
          body * {
            visibility: hidden !important;
            display: none !important;
          }
          body::before {
            content: "Screenshots und Drucken sind nicht erlaubt! Besuche die KI Community für mehr Informationen." !important;
            visibility: visible !important;
            display: block !important;
            text-align: center !important;
            font-size: 24px !important;
            margin-top: 100px !important;
            color: #000 !important;
          }
        }
        
        /* Prevent screenshot tools from capturing content */
        body {
          background-color: #000000 !important;
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        
        /* Overlay protection for screenshot tools */
        body::after {
          content: "" !important;
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          width: 100% !important;
          height: 100% !important;
          background: rgba(0, 0, 0, 0.1) !important;
          pointer-events: none !important;
          z-index: 99999 !important;
          display: none !important;
        }
        
        body.screenshot-blocked::after {
          display: block !important;
        }
      `;
      document.head.appendChild(style);
      return style;
    };

    // Add protection styles
    const protectionStyle = addProtectionStyles();

    // Add event listeners with high priority
    const options = { capture: true, passive: false };
    document.addEventListener('visibilitychange', handleVisibilityChange, options);
    document.addEventListener('keydown', handleKeyDown, options);
    document.addEventListener('keyup', handleKeyUp, options);
    document.addEventListener('contextmenu', handleContextMenu, options);
    document.addEventListener('dragstart', handleDragStart, options);

    // Handle window focus/blur for screenshot detection
    const handleWindowBlur = () => {
      if (!screenshotBlocked) {
        blockScreenshot();
      }
    };

    window.addEventListener('blur', handleWindowBlur);

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('dragstart', handleDragStart);
      window.removeEventListener('blur', handleWindowBlur);
      
      if (protectionStyle && protectionStyle.parentElement) {
        protectionStyle.parentElement.removeChild(protectionStyle);
      }
    };
  }, [screenshotBlocked]);

  // Add body class when screenshot is blocked
  useEffect(() => {
    if (screenshotBlocked) {
      document.body.classList.add('screenshot-blocked');
    } else {
      document.body.classList.remove('screenshot-blocked');
    }
  }, [screenshotBlocked]);

  const handleClick = () => {
    setShowWarning(false);
    setScreenshotBlocked(false);
  };

  return (
    <AnimatePresence>
      {showWarning && (
        <>
          {/* Overlay to prevent interaction */}
          <motion.div
            className="fixed inset-0 bg-black/80 z-[9998] backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={handleClick}
          />
          
          {/* Warning message */}
          <motion.div
            className="fixed z-[9999] bg-gray-900 border-2 border-red-500 rounded-xl shadow-2xl p-8 max-w-md pointer-events-auto cursor-pointer"
            style={{
              left: `${position.x}px`,
              top: `${position.y}px`,
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5),0 10px 10px -5px rgba(0,0,0,0.4)'
            }}
            initial={{ opacity: 0, scale: 0.8, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 10 }}
            transition={{ duration: 0.3 }}
            onClick={handleClick}
          >
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <SafeIcon icon={FiCamera} className="text-red-500 text-2xl" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-4">
                  <SafeIcon icon={FiAlertCircle} className="text-yellow-400 text-xl" />
                  <h3 className="text-white font-black text-xl">SCREENSHOT BLOCKIERT!</h3>
                </div>
                
                <p className="text-gray-300 text-base mb-6 leading-relaxed">
                  <span className="font-bold text-red-400">Achtung:</span> Screenshots sind auf dieser Seite strengstens untersagt! 
                  Diese gloröse Website ist zum Ansehen da, nicht zum Festhalten.
                </p>
                
                <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <SafeIcon icon={FiUsers} className="text-red-400 text-xl" />
                    <p className="text-red-400 font-bold text-base">
                      WILLST DU MEHR WISSEN?
                    </p>
                  </div>
                  <p className="text-gray-300 text-sm mb-3">
                    Wenn du wissen willst, wie dieses Meisterwerk entstanden ist und selbst zum KI-Profi werden willst...
                  </p>
                  <a 
                    href="https://monkeymedia.at/go/kicommunity" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="block bg-gradient-to-r from-red-500 to-orange-500 text-white text-center px-4 py-2 rounded-lg font-bold text-sm hover:opacity-90 transition-opacity"
                  >
                    TRETE DER KI COMMUNITY BEI!
                  </a>
                </div>

                <div className="bg-gray-800/50 rounded-lg p-3 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <SafeIcon icon={FiEyeOff} className="text-gray-400 text-base" />
                    <SafeIcon icon={FiShield} className="text-gray-400 text-base" />
                    <p className="text-gray-500 text-sm font-bold">SCHUTZ AKTIV GEGEN:</p>
                  </div>
                  <div className="text-gray-600 text-xs space-y-1">
                    <p>• Alle Screenshot-Methoden</p>
                    <p>• Print Screen & Shortcuts</p>
                    <p>• Snipping Tools</p>
                    <p>• Drag & Copy</p>
                    <p>• Drucken & Speichern</p>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <p className="text-gray-400 text-xs">Klicke zum Schließen</p>
                  <p className="text-red-400 text-xs font-bold">📸 VERBOTEN!</p>
                </div>
              </div>
            </div>
            
            {/* Animated border effect */}
            <div className="absolute inset-0 rounded-xl border-2 border-red-500/50 pointer-events-none animate-pulse" />
            <div className="absolute -inset-1 rounded-xl border-2 border-red-500/30 pointer-events-none animate-ping" />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default ScreenshotProtection;